import { LocalDate } from "@js-joda/core";
import React from "react";
import useProfileWindowSettings from "../../../profileWindowSettings/useProfileWindowSettings";
import { toEntityCalendarLocalDate } from "./entityProfileCalendar.utils";

/** Keep selected records available to actions even when another month is loaded. */
export default function useCalendarSelection<
  T extends { id: string | number; date: LocalDate | string }
>(items: T[], range: { from: LocalDate; to: LocalDate; isFetched: boolean }) {
  const [{ saveCalendarSelection }] = useProfileWindowSettings();
  const [ids, setIds] = React.useState<T["id"][]>([]);
  const records = React.useRef(new Map<T["id"], T>());
  const selected = ids.flatMap((id) => {
    const item = items.find((candidate) => candidate.id === id) ?? records.current.get(id);
    return item === undefined ? [] : [item];
  });

  React.useEffect(() => {
    if (!range.isFetched) return;
    const loaded = new Map(items.map((item) => [item.id, item]));
    setIds((current) => {
      const next = current.filter((id) => {
        const item = loaded.get(id);
        if (item !== undefined) {
          records.current.set(id, item);
          return true;
        }
        if (!saveCalendarSelection) return false;
        const previous = records.current.get(id);
        if (previous === undefined) return false;
        const date = toEntityCalendarLocalDate(previous.date);
        return date.isBefore(range.from) || date.isAfter(range.to);
      });
      return next.length === current.length ? current : next;
    });
  }, [items, range.from, range.to, range.isFetched, saveCalendarSelection]);

  const replace = (next: T[]) => {
    records.current = new Map(next.map((item) => [item.id, item]));
    setIds(next.map((item) => item.id));
  };
  const toggle = (item: T) => {
    records.current.set(item.id, item);
    setIds((current) =>
      current.includes(item.id) ? current.filter((id) => id !== item.id) : [...current, item.id]
    );
  };

  return { ids, setIds, selected, replace, toggle };
}
